<template>
  <PlatformLayout
    :layout-class="$store.state.role === 'supervisor' ? 'mainStyle3' : 'mainStyle1'"
    :title="pageTitle"
    :show-guest-banner="false"
  >
    <div class="grant-detail-page">
      <button type="button" class="grant-detail-page__back" @click="$router.push('/platform/intGrants')">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Все гранты
      </button>

      <div v-if="loading" class="grant-detail-page__skeleton" aria-busy="true" aria-label="Загрузка информации о гранте">
        <div class="sk sk--grant-hero" />
        <div class="grant-sk-stats">
          <div v-for="n in 4" :key="n" class="sk sk--block" />
        </div>
        <div class="sk sk--block" style="height: 240px" />
        <div class="sk sk--block" style="height: 160px" />
      </div>

      <template v-else-if="grant">
        <header class="grant-hero" :style="{ background: theme.grad }">
          <div class="grant-hero__overlay" />
          <div class="grant-hero__content">
            <div class="grant-hero__logo" v-if="grant.image">
              <img :src="grant.image" :alt="grant.title" />
            </div>
            <div class="grant-hero__text">
              <h1 class="grant-hero__title">{{ grant.title }}</h1>
              <p v-if="grant.subtitle" class="grant-hero__subtitle">{{ grant.subtitle }}</p>
            </div>
          </div>
        </header>

        <div class="grant-stats" v-if="hasStats">
          <div v-if="grant.grant_amount" class="grant-stats__item grant-stats__item--accent">
            <span class="grant-stats__label">Стипендия</span>
            <span class="grant-stats__value">{{ grant.grant_amount }}</span>
          </div>
          <div v-if="grant.country_name" class="grant-stats__item">
            <span class="grant-stats__label">Страна</span>
            <span class="grant-stats__value grant-stats__value--row">
              <img v-if="grant.country_flag" :src="grant.country_flag" alt="" class="grant-stats__flag" />
              {{ grant.country_name }}
            </span>
          </div>
          <div v-if="grant.study_degree_name" class="grant-stats__item">
            <span class="grant-stats__label">Степень</span>
            <span class="grant-stats__value">{{ grant.study_degree_name }}</span>
          </div>
          <div v-if="deadline" class="grant-stats__item">
            <span class="grant-stats__label">Дедлайн</span>
            <span class="grant-stats__value">{{ deadline }}</span>
          </div>
        </div>

        <div class="grant-detail-card">
          <div class="grant-detail-card__actions" v-if="grant.video || grant.hyperlink || grant.guide">
            <a
              v-if="grant.video"
              :href="grant.video"
              target="_blank"
              rel="noopener noreferrer"
              class="grant-detail-card__btn grant-detail-card__btn--primary"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
              Видео о гранте
            </a>
            <a
              v-if="grant.hyperlink"
              :href="grant.hyperlink"
              target="_blank"
              rel="noopener noreferrer"
              class="grant-detail-card__btn grant-detail-card__btn--secondary"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Официальный сайт
            </a>
            <a
              v-if="grant.guide"
              :href="grant.guide"
              target="_blank"
              rel="noopener noreferrer"
              class="grant-detail-card__btn grant-detail-card__btn--secondary"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
              </svg>
              Скачать гайд
            </a>
          </div>

          <div class="grant-detail-card__grid">
            <section class="grant-detail-card__section">
              <h2 class="grant-detail-card__heading">О программе</h2>
              <div
                v-if="grant.description"
                class="grant-detail-card__prose grant-detail-card__prose--desc"
                v-html="grant.description"
              />
              <p v-else class="grant-detail-card__empty">Описание скоро появится</p>

              <template v-if="grant.conditions">
                <h2 class="grant-detail-card__heading grant-detail-card__heading--spaced">Условия участия</h2>
                <div class="grant-detail-card__prose" v-html="grant.conditions" />
              </template>

              <template v-if="grant.content">
                <div class="grant-detail-card__prose grant-detail-card__prose--spaced" v-html="grant.content" />
              </template>
            </section>

            <section
              v-if="covers.length || grant.scholarship_coverage"
              class="grant-detail-card__section grant-detail-card__section--aside"
            >
              <h2 class="grant-detail-card__heading">Что покрывает стипендия</h2>
              <ul v-if="covers.length" class="grant-detail-card__covers">
                <li v-for="item in covers" :key="item" class="grant-detail-card__cover-item">
                  <span class="grant-detail-card__check" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                  <span>{{ item }}</span>
                </li>
              </ul>
              <div
                v-else-if="grant.scholarship_coverage"
                class="grant-detail-card__prose"
                v-html="grant.scholarship_coverage"
              />
            </section>
          </div>
        </div>
      </template>
    </div>
  </PlatformLayout>
</template>

<script>
import PlatformLayout from '@/components/platform/PlatformLayout.vue';
import axios from 'axios';
import { getGrantTheme, parseCoversFromHtml, parseDeadlineFromHtml } from '@/utils/grantTheme';

export default {
  components: {
    PlatformLayout,
  },
  data() {
    return {
      grant: null,
      loading: true,
    };
  },
  computed: {
    pageTitle() {
      if (!this.grant) return 'Грант';
      return `Гранты › ${this.grant.title}`;
    },
    theme() {
      return getGrantTheme(this.grant?.id);
    },
    covers() {
      if (!this.grant) return [];
      const fromDesc = parseCoversFromHtml(this.grant.description);
      if (fromDesc.length) return fromDesc;
      return parseCoversFromHtml(this.grant.scholarship_coverage);
    },
    deadline() {
      return parseDeadlineFromHtml(this.grant?.description);
    },
    hasStats() {
      return this.grant?.grant_amount || this.grant?.country_name || this.grant?.study_degree_name || this.deadline;
    },
  },
  methods: {
    async fetchGrant() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const { data } = await axios.get(`/platform/scholarships/${id}`);
        this.grant = data.data;
      } catch (e) {
        console.error(e);
        this.$router.push('/platform/intGrants');
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchGrant();
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/grants';

.grant-detail-page {
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
  max-width: 960px;

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
    padding: 0;
    border: none;
    background: none;
    color: $grant-text-secondary;
    font-size: 14px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: color 0.15s;

    &:hover {
      color: $grant-accent;
    }
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

@keyframes grant-spin {
  to { transform: rotate(360deg); }
}

.grant-hero {
  position: relative;
  border-radius: $grant-radius-lg;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: $grant-shadow-md;

  &__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.35) 100%);
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 28px 32px;
  }

  &__logo {
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 16px;
    background: #fff;
    padding: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__title {
    margin: 0;
    font-size: 28px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.03em;
    line-height: 1.2;
  }

  &__subtitle {
    margin: 8px 0 0;
    font-size: 15px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.45;
  }

  @media (max-width: 640px) {
    &__content {
      flex-direction: column;
      align-items: flex-start;
      padding: 24px;
    }

    &__title {
      font-size: 22px;
    }

    &__logo {
      width: 64px;
      height: 64px;
    }
  }
}

.grant-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 20px;

  &__item {
    padding: 16px 18px;
    background: $grant-surface;
    border: 1px solid $grant-border;
    border-radius: $grant-radius-md;
    box-shadow: $grant-shadow-sm;

    &--accent {
      background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
      border-color: rgba(232, 25, 44, 0.15);

      .grant-stats__value {
        color: $grant-accent;
        font-size: 20px;
      }
    }
  }

  &__label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $grant-text-muted;
    margin-bottom: 6px;
  }

  &__value {
    font-size: 15px;
    font-weight: 700;
    color: $grant-text;
    line-height: 1.3;

    &--row {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
  }

  &__flag {
    width: 22px;
    height: 15px;
    object-fit: cover;
    border-radius: 2px;
  }
}

.grant-detail-card {
  background: $grant-surface;
  border-radius: $grant-radius-lg;
  border: 1px solid $grant-border;
  box-shadow: $grant-shadow-sm;
  padding: 28px 32px;

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 28px;
    padding-bottom: 24px;
    border-bottom: 1px solid $grant-border;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 20px;
    border-radius: $grant-radius-sm;
    font-size: 14px;
    font-weight: 700;
    font-family: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;

    &:hover {
      transform: translateY(-1px);
    }

    &--primary {
      background: $grant-accent;
      color: #fff;
      border: none;
      box-shadow: 0 2px 8px rgba(232, 25, 44, 0.25);

      &:hover {
        background: #d01528;
      }
    }

    &--secondary {
      background: $grant-bg-subtle;
      color: $grant-text;
      border: 1px solid $grant-border;

      &:hover {
        background: #f0f1f3;
      }
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 32px;
    align-items: start;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__section--aside {
    padding: 20px;
    background: $grant-bg-subtle;
    border-radius: $grant-radius-md;
    border: 1px solid $grant-border;

    @media (max-width: 900px) {
      order: -1;
    }
  }

  &__heading {
    margin: 0 0 16px;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $grant-text-muted;

    &--spaced {
      margin-top: 32px;
    }
  }

  &__empty {
    margin: 0;
    font-size: 15px;
    color: $grant-text-muted;
  }

  &__prose {
    font-size: 15px;
    line-height: 1.75;
    color: $grant-text-secondary;

    &--spaced {
      margin-top: 24px;
    }

    &--desc :deep(ul) {
      display: none;
    }

    :deep(p) {
      margin: 0 0 14px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul) {
      margin: 0 0 14px;
      padding-left: 0;
      list-style: none;
    }

    :deep(li) {
      position: relative;
      padding-left: 0;
      margin-bottom: 8px;
      line-height: 1.6;
    }

    :deep(a) {
      color: #1d4ed8;
      text-decoration: underline;
      text-underline-offset: 2px;

      &:hover {
        color: #1e40af;
      }
    }

    :deep(strong),
    :deep(b) {
      font-weight: 700;
      color: $grant-text;
    }

    :deep(br) {
      display: block;
      content: '';
      margin-top: 6px;
    }
  }

  &__covers {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__cover-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 14px;
    font-weight: 600;
    color: $grant-text;
    line-height: 1.45;
  }

  &__check {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(232, 25, 44, 0.1);
    color: $grant-accent;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
  }

  @media (max-width: 640px) {
    padding: 20px;

    &__actions {
      flex-direction: column;
    }

    &__btn {
      justify-content: center;
      width: 100%;
    }
  }
}
</style>
